import React from 'react';
import {
  Box,
  Typography,
  Chip,
  Paper,
  Tabs,
  Tab,
  Stack,
  Divider,
  Grid,
  Avatar,
  IconButton,
  Button,
} from '@mui/material';
import {
  Notifications as NotificationsIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Schedule as ScheduleIcon,
  MarkEmailRead as ReadIcon,
  MarkEmailUnread as UnreadIcon,
  PriorityHigh as PriorityIcon,
  Email as EmailIcon,
  Sms as SmsIcon,
  NotificationsActive as PushIcon,
  Info as InfoIcon,
  Warning as WarningIcon,
  Error as ErrorIcon,
  CheckCircle as SuccessIcon,
} from '@mui/icons-material';
import { formatDate, formatDateTime } from '../../../utils/formatters.js';

const priorityColors = {
  low: 'default',
  normal: 'primary',
  high: 'warning',
  urgent: 'error',
};

const typeColors = {
  info: 'info',
  warning: 'warning',
  error: 'error',
  success: 'success',
  general: 'default',
};

const channelIcons = {
  'in-app': NotificationsIcon,
  email: EmailIcon,
  sms: SmsIcon,
  push: PushIcon,
};

const typeIcons = {
  info: InfoIcon,
  warning: WarningIcon,
  error: ErrorIcon,
  success: SuccessIcon,
  general: NotificationsIcon,
};

function NotificationDetail({
  notification, onEdit, onDelete, onMarkRead, onMarkUnread,
}) {
  const [tabValue, setTabValue] = React.useState(0);

  if (!notification) {
    return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <Typography variant="h6" color="text.secondary">
          Select a notification to view details
        </Typography>
      </Box>
    );
  }

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  const ChannelIcon = channelIcons[notification.channel] || NotificationsIcon;
  const TypeIcon = typeIcons[notification.type] || NotificationsIcon;

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* Header */}
      <Paper sx={{ p: 3, mb: 2 }}>
        <Stack direction="row" justifyContent="space-between" alignItems="flex-start" sx={{ mb: 2 }}>
          <Box sx={{ flex: 1 }}>
            <Typography variant="h5" component="h2" gutterBottom>
              Notification Details
            </Typography>
            <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
              <Chip
                label={notification.type || 'General'}
                color={typeColors[notification.type] || 'default'}
                size="small"
                icon={<TypeIcon />}
              />
              <Chip
                label={notification.priority || 'Normal'}
                color={priorityColors[notification.priority] || 'default'}
                size="small"
                icon={<PriorityIcon />}
              />
              <Chip
                label={notification.read ? 'Read' : 'Unread'}
                color={notification.read ? 'success' : 'warning'}
                size="small"
                icon={notification.read ? <ReadIcon /> : <UnreadIcon />}
              />
            </Stack>
          </Box>
          <Stack direction="row" spacing={1}>
            <IconButton
              onClick={() => (notification.read ? onMarkUnread(notification) : onMarkRead(notification))}
              color={notification.read ? 'warning' : 'success'}
            >
              {notification.read ? <UnreadIcon /> : <ReadIcon />}
            </IconButton>
            <IconButton onClick={() => onEdit(notification)} color="primary">
              <EditIcon />
            </IconButton>
            <IconButton onClick={() => onDelete(notification)} color="error">
              <DeleteIcon />
            </IconButton>
          </Stack>
        </Stack>

        {notification.message && (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {notification.message}
          </Typography>
        )}
      </Paper>

      {/* Tabs */}
      <Paper sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
        <Tabs value={tabValue} onChange={handleTabChange} sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tab label="Overview" />
          <Tab label="Timeline" />
          <Tab label="Settings" />
        </Tabs>

        {/* Tab Content */}
        <Box sx={{ flex: 1, p: 3, overflow: 'auto' }}>
          {tabValue === 0 && (
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <Stack spacing={2}>
                  <Box>
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                      Message
                    </Typography>
                    <Typography variant="body2">
                      {notification.message || 'No message content'}
                    </Typography>
                  </Box>

                  <Box>
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                      Type
                    </Typography>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <TypeIcon fontSize="small" color="action" />
                      <Typography variant="body2">
                        {notification.type || 'General'}
                      </Typography>
                    </Stack>
                  </Box>

                  <Box>
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                      Priority
                    </Typography>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <PriorityIcon fontSize="small" color="action" />
                      <Typography variant="body2">
                        {notification.priority || 'Normal'}
                      </Typography>
                    </Stack>
                  </Box>
                </Stack>
              </Grid>

              <Grid item xs={12} md={6}>
                <Stack spacing={2}>
                  <Box>
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                      Channel
                    </Typography>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <ChannelIcon fontSize="small" color="action" />
                      <Typography variant="body2">
                        {notification.channel || 'In-App'}
                      </Typography>
                    </Stack>
                  </Box>

                  <Box>
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                      Related To
                    </Typography>
                    <Typography variant="body2">
                      {notification.related || 'Not specified'}
                    </Typography>
                  </Box>

                  <Box>
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                      Scheduled Time
                    </Typography>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <ScheduleIcon fontSize="small" color="action" />
                      <Typography variant="body2">
                        {notification.time ? formatDate(notification.time) : 'Not scheduled'}
                      </Typography>
                    </Stack>
                  </Box>
                </Stack>
              </Grid>

              <Grid item xs={12}>
                <Divider sx={{ my: 2 }} />
                <Box>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    Created
                  </Typography>
                  <Typography variant="body2">
                    {notification.createdAt ? formatDateTime(notification.createdAt) : 'Unknown'}
                  </Typography>
                </Box>
              </Grid>
            </Grid>
          )}

          {tabValue === 1 && (
            <Box>
              <Typography variant="h6" gutterBottom>
                Notification Timeline
              </Typography>
              <Stack spacing={2}>
                <Paper sx={{ p: 2 }}>
                  <Stack direction="row" spacing={2} alignItems="center">
                    <Avatar sx={{ bgcolor: 'primary.main', width: 32, height: 32 }}>
                      <NotificationsIcon fontSize="small" />
                    </Avatar>
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="body2" fontWeight="medium">
                        Notification Created
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {notification.createdAt ? formatDateTime(notification.createdAt) : 'Unknown'}
                      </Typography>
                    </Box>
                  </Stack>
                </Paper>

                {notification.time && (
                  <Paper sx={{ p: 2 }}>
                    <Stack direction="row" spacing={2} alignItems="center">
                      <Avatar sx={{ bgcolor: 'warning.main', width: 32, height: 32 }}>
                        <ScheduleIcon fontSize="small" />
                      </Avatar>
                      <Box sx={{ flex: 1 }}>
                        <Typography variant="body2" fontWeight="medium">
                          Scheduled for Delivery
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {formatDate(notification.time)}
                        </Typography>
                      </Box>
                    </Stack>
                  </Paper>
                )}

                {notification.read && (
                  <Paper sx={{ p: 2 }}>
                    <Stack direction="row" spacing={2} alignItems="center">
                      <Avatar sx={{ bgcolor: 'success.main', width: 32, height: 32 }}>
                        <ReadIcon fontSize="small" />
                      </Avatar>
                      <Box sx={{ flex: 1 }}>
                        <Typography variant="body2" fontWeight="medium">
                          Notification Read
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Marked as read by user
                        </Typography>
                      </Box>
                    </Stack>
                  </Paper>
                )}
              </Stack>
            </Box>
          )}

          {tabValue === 2 && (
            <Box>
              <Typography variant="h6" gutterBottom>
                Notification Settings
              </Typography>
              <Stack spacing={2}>
                <Paper sx={{ p: 2 }}>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    Delivery Configuration
                  </Typography>
                  <Stack spacing={1}>
                    <Box>
                      <Typography variant="body2" fontWeight="medium">
                        Channel:
                        {' '}
                        {notification.channel || 'In-App'}
                      </Typography>
                    </Box>
                    <Box>
                      <Typography variant="body2" fontWeight="medium">
                        Priority:
                        {' '}
                        {notification.priority || 'Normal'}
                      </Typography>
                    </Box>
                    <Box>
                      <Typography variant="body2" fontWeight="medium">
                        Type:
                        {' '}
                        {notification.type || 'General'}
                      </Typography>
                    </Box>
                  </Stack>
                </Paper>

                <Paper sx={{ p: 2 }}>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    Content
                  </Typography>
                  <Typography variant="body2">
                    {notification.message || 'No message content available.'}
                  </Typography>
                </Paper>

                <Paper sx={{ p: 2 }}>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    Related Entity
                  </Typography>
                  <Typography variant="body2">
                    {notification.related || 'No related entity specified.'}
                  </Typography>
                </Paper>

                <Paper sx={{ p: 2 }}>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    Status Information
                  </Typography>
                  <Stack spacing={1}>
                    <Box>
                      <Typography variant="body2" fontWeight="medium">
                        Read Status:
                        {' '}
                        {notification.read ? 'Read' : 'Unread'}
                      </Typography>
                    </Box>
                    <Box>
                      <Typography variant="body2" fontWeight="medium">
                        Created:
                        {' '}
                        {notification.createdAt ? formatDateTime(notification.createdAt) : 'Unknown'}
                      </Typography>
                    </Box>
                    {notification.time && (
                      <Box>
                        <Typography variant="body2" fontWeight="medium">
                          Scheduled:
                          {' '}
                          {formatDate(notification.time)}
                        </Typography>
                      </Box>
                    )}
                  </Stack>
                </Paper>
              </Stack>
            </Box>
          )}
        </Box>
      </Paper>
    </Box>
  );
}

export default NotificationDetail;
